import { useEffect, useMemo, useState } from "react"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemText,
  MenuItem,
  TextField,
  Tooltip
} from "@mui/material"
import SaveIcon from "@mui/icons-material/Save"
import LinkOffIcon from "@mui/icons-material/LinkOff"
import { ColumnDef } from "@tanstack/react-table"
import { toast } from "react-toastify"

import { DataGridMui } from "src/shared/container/data-grid-mui"
import {
  IntegrationLocationsDocument,
  useIntegrationExternalLocationsQuery,
  useIntegrationLocationsQuery,
  useRemoveIntegrationLocationMutation,
  useSetIntegrationLocationMutation
} from "src/graphql/quark/quark.type"
import { FEATURE_EXTERNAL_LOCATION_PICKER } from "../connectorFeatures"

type LocationRow = {
  locationId: string
  name: string
  externalVendorId?: string | null
}

type ExternalLocation = {
  id: string
  name: string
  address?: string | null
  isActive: boolean
  linkedLocationId?: string | null
}

/**
 * Owns its own keystroke state so typing never touches LocationsTab — that
 * previously rebuilt the whole columns array (and the table under it) on
 * every character.
 */
const VendorIdCell = ({
  row,
  canEdit,
  saving,
  externalLocations,
  onSave,
  onRemove
}: {
  row: LocationRow
  canEdit: boolean
  saving: boolean
  externalLocations?: ExternalLocation[]
  onSave: (row: LocationRow, value: string) => Promise<void>
  onRemove: (row: LocationRow) => void
}) => {
  const [value, setValue] = useState(row.externalVendorId ?? "")
  const dirty = value.trim() !== (row.externalVendorId ?? "")
  const current = externalLocations?.find((l) => l.id === row.externalVendorId)

  // Resyncs after a remote change (save confirmation, unlink) without
  // reacting to local typing, since that only ever updates `value` directly.
  useEffect(() => {
    setValue(row.externalVendorId ?? "")
  }, [row.externalVendorId])

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      {externalLocations ? (
        <TextField
          select
          size="small"
          fullWidth
          disabled={!canEdit}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected) => {
              if (!selected) return <Box color="text.disabled">Not published</Box>
              return externalLocations.find((l) => l.id === selected)?.name ?? String(selected)
            }
          }}
        >
          {externalLocations
            .filter((l) => l.isActive || l.id === row.externalVendorId)
            .map((l) => (
              <MenuItem
                key={l.id}
                value={l.id}
                disabled={!!l.linkedLocationId && l.linkedLocationId !== row.locationId}
              >
                <ListItemText
                  primary={l.name}
                  secondary={
                    l.linkedLocationId && l.linkedLocationId !== row.locationId ? "Linked to another store" : l.address
                  }
                />
              </MenuItem>
            ))}
          {row.externalVendorId && !current && <MenuItem value={row.externalVendorId}>{row.externalVendorId}</MenuItem>}
        </TextField>
      ) : (
        <TextField
          size="small"
          fullWidth
          disabled={!canEdit}
          placeholder="Not published"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && dirty) void onSave(row, value)
          }}
        />
      )}
      <Tooltip title="Save">
        <span>
          <IconButton
            size="small"
            color="primary"
            disabled={!canEdit || saving || !dirty}
            onClick={() => void onSave(row, value)}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Stop publishing this store">
        <span>
          <IconButton
            size="small"
            color="error"
            disabled={!canEdit || !row.externalVendorId}
            onClick={() => onRemove(row)}
          >
            <LinkOffIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}

const LocationsTab = ({
  connectionId,
  channelName,
  features,
  canEdit
}: {
  connectionId: string
  channelName: string
  features: string[]
  canEdit: boolean
}) => {
  const usesPicker = features.includes(FEATURE_EXTERNAL_LOCATION_PICKER)
  const [unlinking, setUnlinking] = useState<LocationRow | null>(null)

  const { data, loading } = useIntegrationLocationsQuery({
    variables: { connectionId },
    fetchPolicy: "network-only"
  })

  const { data: externalData, loading: externalLoading } = useIntegrationExternalLocationsQuery({
    variables: { connectionId },
    fetchPolicy: "network-only",
    skip: !usesPicker
  })

  // Which store holds each channel location comes from our own links, so a
  // save refetches only those rather than the channel's live location list.
  const externalLocations: ExternalLocation[] | undefined = useMemo(() => {
    if (!usesPicker) return undefined
    const linkedBy = new Map(
      (data?.fusionIntegrationLocations ?? [])
        .filter((l) => l.externalVendorId)
        .map((l) => [l.externalVendorId as string, l.locationId])
    )
    return (externalData?.fusionIntegrationExternalLocations ?? []).map((l) => ({
      ...l,
      linkedLocationId: linkedBy.get(l.id) ?? null
    }))
  }, [usesPicker, externalData, data])

  const refetchQueries = [{ query: IntegrationLocationsDocument, variables: { connectionId } }]

  const [setLocation, { loading: saving }] = useSetIntegrationLocationMutation({
    refetchQueries
  })
  const [removeLocation] = useRemoveIntegrationLocationMutation({
    refetchQueries
  })

  const rows: LocationRow[] = data?.fusionIntegrationLocations ?? []

  const handleSave = async (row: LocationRow, value: string) => {
    const externalVendorId = value.trim()
    if (!externalVendorId) {
      toast.error(
        usesPicker
          ? "Choose a location, or unlink to stop publishing this store."
          : "Enter the vendor ID, or unlink to stop publishing this store."
      )
      return
    }

    try {
      await setLocation({
        variables: {
          input: { connectionId, locationId: row.locationId, externalVendorId }
        }
      })
      const linkedName = externalLocations?.find((l) => l.id === externalVendorId)?.name ?? externalVendorId
      toast.success(`${row.name} linked to ${linkedName}`)
    } catch (error: any) {
      toast.error(error?.message ?? "Could not save the vendor ID")
    }
  }

  const handleRemove = async (row: LocationRow) => {
    try {
      await removeLocation({
        variables: { input: { connectionId, oneObjectId: row.locationId } }
      })
      toast.success(`${row.name} is no longer published`)
    } catch (error: any) {
      toast.error(error?.message ?? "Could not unlink the store")
    }
  }

  const requestRemove = (row: LocationRow) => {
    if (usesPicker) setUnlinking(row)
    else void handleRemove(row)
  }

  const columns: ColumnDef<LocationRow, any>[] = useMemo(
    () => [
      { header: "Store", accessorKey: "name" },
      {
        header: usesPicker ? "Channel location" : "Vendor ID on this integration",
        accessorKey: "externalVendorId",
        cell: ({ row }) => (
          <VendorIdCell
            row={row.original}
            canEdit={canEdit}
            saving={saving}
            externalLocations={externalLocations}
            onSave={handleSave}
            onRemove={requestRemove}
          />
        )
      }
    ],
    [canEdit, saving, usesPicker, externalLocations]
  )

  return (
    <>
      <DataGridMui
        id="integration-locations"
        data={rows}
        columns={columns}
        isLoading={loading || externalLoading}
        paginationMode="client"
        // initialState is captured once at mount, before rows load — a fixed
        // ceiling avoids seeding pageSize from an empty first-render array.
        initialPageSize={1000}
        // LocationRow has no `id`; without this every row falls back to the
        // same undefined key, so React remounts the cell being edited on every keystroke.
        getRowId={(row: LocationRow) => row.locationId}
        hidePagination
        noDataComponent={<Box p={3}>No stores found.</Box>}
      />

      <Dialog open={!!unlinking} onClose={() => setUnlinking(null)} fullWidth maxWidth="xs">
        <DialogTitle>Unlink {unlinking?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Stock at this {channelName} location will be set to 0 before it is unlinked.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlinking(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (unlinking) void handleRemove(unlinking)
              setUnlinking(null)
            }}
          >
            Unlink
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default LocationsTab
